import {
  type V2AdminNegotiationDetailDto,
  type V2AdminNegotiationsPageDto,
  type V2AdminNegotiationsSummaryDto,
  type V2MyStateTransitionsDto,
  type V2NegotiationActivationResultDto,
  v2AdminNegotiationsControllerAdvance,
  v2AdminNegotiationsControllerDetail,
  v2AdminNegotiationsControllerInactivate,
  v2AdminNegotiationsControllerList,
  v2AdminNegotiationsControllerReactivate,
  v2AdminNegotiationsControllerReject,
  v2AdminNegotiationsControllerRescind,
  v2AdminNegotiationsControllerSummary,
  v2MeStateTransitionsControllerList
} from '@idc-forms/api-client'
import {
  V2AdminNegotiationDetailSchema,
  V2AdminNegotiationsPageSchema,
  V2AdminNegotiationsSummarySchema,
  V2NegotiationActivationResultSchema
} from '@idc-forms/schemas/api-v2/admin-negotiations'
import { V2MyStateTransitionsSchema } from '@idc-forms/schemas/api-v2/me-state-transitions'
import { assertShape } from '@/shared/api/assert-shape'
import { bearer, v2RequestOptions } from '@/shared/api/v2-request'
import type { NegotiationSort } from '../components/negotiations-filter-tabs'

/**
 * El tablero de negociaciones contra la API v2 (ADR 0019/0020).
 *
 * TRES LLAMADAS QUE HABLAN LA MISMA LENGUA, y ese es el punto de la tajada: el
 * listado filtra por `sta_`, los conteos vienen indexados por `sta_` y los chips
 * salen de `me/state-transitions`, que también devuelve `sta_`. En v1 las tres
 * hablaban el **uuid legacy**, así que migrar una sola habría hecho que el
 * filtro no encontrara nada y todos los conteos cayeran a cero **sin dar
 * error** — es la trampa que el ADR 0027 documentó para los selectores del
 * expediente y la razón por la que estas tres viajan juntas.
 */

export type NegotiationsAdminV2Query = {
  filters?: Record<string, unknown>
  /** `campo:asc|desc` (un solo campo; el cursor desempata por id). */
  sort?: NegotiationSort
  search?: string
  pageSize?: number
  after?: string
  before?: string
}

export type NegotiationsAdminV2Row = V2AdminNegotiationsPageDto['data'][number]
export type NegotiationsV2Summary = V2AdminNegotiationsSummaryDto
export type MyStateTransition = V2MyStateTransitionsDto['data'][number]

function toListQuery(query: NegotiationsAdminV2Query) {
  return {
    ...(query.filters && Object.keys(query.filters).length ? { filters: query.filters } : {}),
    ...(query.sort ? { sort: query.sort } : {}),
    ...(query.search ? { search: query.search } : {}),
    ...(query.pageSize ? { 'page[size]': query.pageSize } : {}),
    ...(query.after ? { 'page[after]': query.after } : {}),
    ...(query.before ? { 'page[before]': query.before } : {})
  }
}

export const negotiationsAdminV2Service = {
  list: async (
    accessToken: string,
    query: NegotiationsAdminV2Query
  ): Promise<V2AdminNegotiationsPageDto> => {
    const result = await v2AdminNegotiationsControllerList({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      query: toListQuery(query)
    })
    return assertShape(V2AdminNegotiationsPageSchema, result.data)
  },

  /**
   * Los totales van aparte del listado por decisión del ADR 0019, no por
   * comodidad: así móvil, PAT y las integraciones que sólo recorren la colección
   * no pagan por contarla.
   */
  /**
   * El DETALLE de un expediente. Sucede a `GET /negotiations/:id` de v1.
   *
   * Se direcciona por `neg_`: v2 no acepta el `_id` ni lo publica. La forma también cambia —
   * `id` en vez de `publicId`, referencias con nombre en vez de documentos embebidos, y los
   * cuatro montos sin impuesto en `null` para quien no tenga `FieldAmountsWithoutTaxNegotiation`,
   * que en v1 viajaban a cualquiera que pudiera ver el expediente.
   */
  detail: async (accessToken: string, id: string): Promise<V2AdminNegotiationDetailDto> => {
    const result = await v2AdminNegotiationsControllerDetail({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id }
    })
    return assertShape(V2AdminNegotiationDetailSchema, result.data)
  },

  summary: async (
    accessToken: string,
    query: Pick<NegotiationsAdminV2Query, 'filters' | 'search'>
  ): Promise<V2AdminNegotiationsSummaryDto> => {
    const result = await v2AdminNegotiationsControllerSummary({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      query: toListQuery(query)
    })
    return assertShape(V2AdminNegotiationsSummarySchema, result.data)
  },

  /**
   * Los estados que ESTE usuario puede leer. Sucede a `GET /states/my-transitions`.
   *
   * Devuelve cosas distintas según quién llame, así que no se cachea por más
   * tiempo que la sesión: un cambio de rol tiene que verse al recargar.
   */
  myStateTransitions: async (accessToken: string): Promise<V2MyStateTransitionsDto> => {
    const result = await v2MeStateTransitionsControllerList({
      ...v2RequestOptions,
      headers: bearer(accessToken)
    })
    return assertShape(V2MyStateTransitionsSchema, result.data)
  },

  /**
   * Apaga la negociación. El estado destino NO se manda: hay un solo estado de
   * tipo `inactive` y lo resuelve el servidor.
   */
  inactivate: async (
    accessToken: string,
    id: string,
    comment?: string
  ): Promise<V2NegotiationActivationResultDto> => {
    const result = await v2AdminNegotiationsControllerInactivate({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id },
      body: comment ? { comment } : {}
    })
    return assertShape(V2NegotiationActivationResultSchema, result.data)
  },

  /**
   * La devuelve al último estado antes de apagarse, leído del historial.
   *
   * Puede responder **409 `PROPERTY_HAS_ACTIVE_NEGOTIATION`**: mientras estuvo
   * inactiva no retenía su inmueble, así que otra negociación pudo tomarlo. No
   * es un error del usuario que reactiva — es que el inmueble ya no está libre—,
   * y por eso la pantalla lo cuenta con el código de la negociación que bloquea.
   */
  reactivate: async (
    accessToken: string,
    id: string,
    comment?: string
  ): Promise<V2NegotiationActivationResultDto> => {
    const result = await v2AdminNegotiationsControllerReactivate({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id },
      body: comment ? { comment } : {}
    })
    return assertShape(V2NegotiationActivationResultSchema, result.data)
  },

  /**
   * La devuelve un paso atrás en su flujo. «Rechazar» no cierra nada: marca el
   * expediente como devuelto en el estado al que vuelve, para que lo corrijan.
   *
   * El paso destino sale del WORKFLOW y no viaja en el cuerpo — devolver es
   * retroceder en el flujo definido, no elegir a dónde mandarlo.
   */
  reject: async (
    accessToken: string,
    id: string,
    comment?: string
  ): Promise<V2NegotiationActivationResultDto> => {
    const result = await v2AdminNegotiationsControllerReject({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id },
      body: comment ? { comment } : {}
    })
    return assertShape(V2NegotiationActivationResultSchema, result.data)
  },

  /**
   * La venta se cayó: suelta el inmueble, corta el flujo, cierra la fecha final y
   * le quita al cliente el acceso móvil a ese inmueble.
   *
   * Rescindir una venta ya APROBADA pide `RescindApprovedNegotiation`; si falta,
   * responde 403 `NEGOTIATION_RESCIND_APPROVED_FORBIDDEN`.
   */
  rescind: async (
    accessToken: string,
    id: string,
    comment?: string
  ): Promise<V2NegotiationActivationResultDto> => {
    const result = await v2AdminNegotiationsControllerRescind({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id },
      body: comment ? { comment } : {}
    })
    return assertShape(V2NegotiationActivationResultSchema, result.data)
  },

  /**
   * Avanza al siguiente paso, o CIERRA la venta si no hay siguiente.
   *
   * Puede responder **400 `NEGOTIATION_MISSING_REQUIRED_ATTACHMENTS`** con la
   * lista de lo que falta en `details.missingAttachments`: la pantalla los nombra
   * en vez de decir «no se puede avanzar».
   */
  advance: async (
    accessToken: string,
    id: string,
    comment?: string
  ): Promise<V2NegotiationActivationResultDto> => {
    const result = await v2AdminNegotiationsControllerAdvance({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id },
      body: comment ? { comment } : {}
    })
    return assertShape(V2NegotiationActivationResultSchema, result.data)
  }
}
